import { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Mail, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { supabase } from "@/lib/supabase";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      // await axios.post(`${API_URL}/api/contact`, formData);
      const { error } = await supabase.functions.invoke("raindas-technologies-limited-website-contact-form", {
        body: formData,
      });
      if (error) throw error;
      toast.success("Message sent successfully");
      setFormData({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Failed to send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="contact-page">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1535391879778-3bae11d29a24?auto=format&fit=crop&w=2000&q=80"
            alt="Futuristic city"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/95 to-obsidian" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-mint mb-6 opacity-0 animate-fade-up">
              Contact
            </p>
            <h1 className="text-5xl sm:text-6xl md:text-7xl font-heading font-bold text-white tracking-tighter leading-[0.9] opacity-0 animate-fade-up animate-delay-100">
              Let's build
              <br />
              <span className="text-mint">together.</span>
            </h1>
            <p className="mt-10 text-xl text-slate-400 leading-relaxed opacity-0 animate-fade-up animate-delay-200">
              Have a project in mind or want to learn more about our products and services? We'd be glad to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 md:py-24 bg-obsidian-light" data-testid="contact-form-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-4">
              <h2 className="text-2xl font-heading font-bold text-white mb-10">
                Contact Information
              </h2>

              <div className="space-y-8">
                <div>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-600 mb-3">
                    Email
                  </p>
                  <a
                    href="mailto:hello@raindas.co"
                    className="block text-white hover:text-mint transition-colors text-lg mb-2"
                    data-testid="contact-email-finance"
                  >
                    hello@raindas.co
                  </a>
                  <a
                    href="mailto:studio@raindas.co"
                    className="block text-white hover:text-mint transition-colors text-lg"
                    data-testid="contact-email-compliance"
                  >
                    studio@raindas.co
                  </a>
                </div>

                <div>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-600 mb-3">
                    Domain
                  </p>
                  <p className="text-white text-lg">raindas.co</p>
                </div>
              </div>

              <div className="mt-12 p-8 bg-white/5 border border-white/5">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-mint mb-4">
                  Response Time
                </p>
                <p className="text-slate-400 leading-relaxed">
                  We typically respond to inquiries within 24-48 business hours. For urgent matters, indicate so in your message.
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-8">
              <div className="glass-card p-8 md:p-12">
                <h2 className="text-2xl font-heading font-bold text-white mb-10">
                  Send us a message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="name" className="text-slate-300 text-sm font-medium">
                        Name <span className="text-coral">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-mint focus:ring-1 focus:ring-mint rounded-none h-12"
                        placeholder="Your name"
                        data-testid="contact-input-name"
                      />
                    </div>

                    <div className="space-y-3">
                      <Label htmlFor="email" className="text-slate-300 text-sm font-medium">
                        Email <span className="text-coral">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-mint focus:ring-1 focus:ring-mint rounded-none h-12"
                        placeholder="you@company.com"
                        data-testid="contact-input-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label htmlFor="company" className="text-slate-300 text-sm font-medium">
                        Company
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-mint focus:ring-1 focus:ring-mint rounded-none h-12"
                        placeholder="Your company"
                        data-testid="contact-input-company"
                      />
                    </div>

                    <div className="space-y-3">
                      <Label htmlFor="subject" className="text-slate-300 text-sm font-medium">
                        Subject <span className="text-coral">*</span>
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        type="text"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-mint focus:ring-1 focus:ring-mint rounded-none h-12"
                        placeholder="What is this regarding?"
                        data-testid="contact-input-subject"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label htmlFor="message" className="text-slate-300 text-sm font-medium">
                      Message <span className="text-cyber">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus:border-electric focus:ring-1 focus:ring-electric rounded-none resize-none"
                      placeholder="Tell us about your project or inquiry..."
                      data-testid="contact-input-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary group flex items-center gap-3 bg-mint text-black px-8 py-5 font-bold text-sm tracking-wide uppercase hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
